'use client'

import { useIsDesktop } from '../../hooks'
import React, { useRef, useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../dialog/dialog'
import { Icon } from '../icon'
import { Row } from '../row'
import { twMerge } from 'tailwind-merge'
import { useDirection } from '../../hooks'

export interface PreviewProps {
  images: string[]
  open: boolean
  initialIndex?: number
  onClose: () => void
}

export const Preview = ({
  images,
  open,
  initialIndex = 0,
  onClose,
}: PreviewProps) => {
  const direction = useDirection()
  const [current, setCurrent] = useState(initialIndex)
  const [fade, setFade] = useState<'in' | 'out'>('in')
  const [displayImage, setDisplayImage] = useState<string>('')
  const [swipeProgress, setSwipeProgress] = useState(0)
  const isDesktop = useIsDesktop()
  const count = images?.length

  // Swipe gesture state
  const touchStartX = useRef<number | null>(null)
  const touchEndX = useRef<number | null>(null)
  const touchStartY = useRef<number | null>(null)
  const touchEndY = useRef<number | null>(null)
  const SWIPE_THRESHOLD = 50
  const SWIPE_DOWN_THRESHOLD = 100
  const MAX_SWIPE_DOWN = 200 // Maximum swipe distance for full animation

  const isRtl = direction === 'rtl'

  // Calculate transform styles based on swipe progress
  const getDialogTransform = useCallback(() => {
    if (swipeProgress === 0) return {}

    // Scale from 1 to 0.9 (90% width)
    const scale = 1 - swipeProgress * 0.05

    // Move from center to 10% from top
    const translateY = swipeProgress * 10 // 40vh movement

    return {
      transform: `scale(${scale}) translateY(${translateY}vh)`,
      transition: swipeProgress > 0 ? 'none' : 'transform 0.3s ease-out',
    }
  }, [swipeProgress])

  // Animation logic
  const animateTo = useCallback(
    (nextIdx: number) => {
      setFade('out')
      setTimeout(() => {
        setCurrent(nextIdx)
        setDisplayImage(images[nextIdx] || '')
        setFade('in')
      }, 180) // duration should match CSS
    },
    [images],
  )

  const goPrev = useCallback(
    () => animateTo(current === 0 ? count - 1 : current - 1),
    [current, count, animateTo],
  )
  const goNext = useCallback(
    () => animateTo(current === count - 1 ? 0 : current + 1),
    [current, count, animateTo],
  )

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!open) return

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault()
          if (isRtl) {
            goNext()
          } else {
            goPrev()
          }
          break
        case 'ArrowRight':
          event.preventDefault()
          if (isRtl) {
            goPrev()
          } else {
            goNext()
          }
          break
        case 'Escape':
          event.preventDefault()
          onClose()
          break
      }
    }

    if (open) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, isRtl, onClose, goPrev, goNext])

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches && e.touches[0]) {
      touchStartX.current = e.touches[0].clientX
      touchStartY.current = e.touches[0].clientY
    }
  }
  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches && e.touches[0]) {
      touchEndX.current = e.touches[0].clientX
      touchEndY.current = e.touches[0].clientY

      // Calculate swipe progress for animation
      if (touchStartY.current !== null && touchEndY.current !== null) {
        const diffY = touchEndY.current - touchStartY.current
        if (diffY > 0) {
          // Only for downward swipes
          const progress = Math.min(diffY / MAX_SWIPE_DOWN, 1)
          setSwipeProgress(progress)
        }
      }
    }
  }
  const handleTouchEnd = () => {
    if (
      touchStartX.current !== null &&
      touchEndX.current !== null &&
      touchStartY.current !== null &&
      touchEndY.current !== null
    ) {
      const diffX = touchStartX.current - touchEndX.current
      const diffY = touchEndY.current - touchStartY.current

      // Check for vertical swipe down (close dialog)
      if (diffY > SWIPE_DOWN_THRESHOLD && Math.abs(diffX) < SWIPE_THRESHOLD) {
        // If swipe progress is high enough, close immediately
        if (swipeProgress > 0.05) {
          onClose()
        } else {
          // Otherwise, animate back to original position
          setSwipeProgress(0)
        }
        return
      }

      // Check for horizontal swipe (navigate images)
      if (
        Math.abs(diffX) > SWIPE_THRESHOLD &&
        Math.abs(diffY) < SWIPE_THRESHOLD
      ) {
        if (diffX > SWIPE_THRESHOLD) {
          goNext()
        } else if (diffX < -SWIPE_THRESHOLD) {
          goPrev()
        }
      }
    }

    // Reset touch values
    touchStartX.current = null
    touchEndX.current = null
    touchStartY.current = null
    touchEndY.current = null

    // Only reset animation if it's not a close gesture
    if (swipeProgress <= 0.5) {
      setSwipeProgress(0)
    }
  }

  React.useEffect(() => {
    if (open) {
      setCurrent(initialIndex)
      setDisplayImage(images[initialIndex] || '')
      setSwipeProgress(0) // Reset swipe progress when dialog opens
    }
  }, [open, initialIndex, images])

  if (count === 0) return null

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className='!text-white bg-black border-none p-0 min-w-[100vw] min-h-[100vh] rounded-none flex flex-col items-center justify-center'
        style={getDialogTransform()}
      >
        <DialogHeader>
          <DialogTitle />
        </DialogHeader>
        <DialogClose className='absolute top-sm right-sm z-10 text-white cursor-pointer' />
        <div
          className='flex-1 flex items-center justify-center w-[100vw] h-[100vh]'
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <img
            src={displayImage}
            alt={`preview-image-${current}`}
            className={`object-contain max-h-[100vh] max-w-[100vw] md:max-h-[70vh] md:max-w-[80vw] rounded-md transition-opacity duration-200 ${fade === 'in' ? 'opacity-100' : 'opacity-0'}`}
            style={{
              maxHeight: isDesktop ? '70vh' : '100vh',
              maxWidth: isDesktop ? '80vw' : '100vw',
              objectFit: 'contain',
              borderRadius: isDesktop ? 'var(--radius-md)' : '0',
            }}
            key={current}
            loading='eager'
          />
        </div>
        <Row className='w-full items-center justify-center gap-md py-md'>
          <button
            onClick={goPrev}
            disabled={count <= 1}
            className='cursor-pointer rounded-full'
          >
            <Icon name='ChevronRight' color='fill-white-100' />
          </button>
          <div
            className={twMerge(
              'flex gap-xs items-center !text-white',
              isRtl ? 'rtl' : 'ltr',
            )}
          >
            {images?.map((_, idx) => (
              <span
                onClick={() => animateTo(idx)}
                key={idx}
                className={`text-white-100 cursor-pointer inline-block rounded-full bg-white-100 transition-all ${
                  idx === current ? 'w-4 h-2' : 'w-2 h-2 opacity-50'
                }`}
              />
            ))}
          </div>
          <button
            onClick={goNext}
            disabled={count <= 1}
            className='cursor-pointer rounded-full fill-neutral-surface-100 !border-none !outline-none'
          >
            <Icon name='ChevronLeft' color='fill-white-100' />
          </button>
        </Row>
      </DialogContent>
    </Dialog>
  )
}
